import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import { SemboDate } from '@libs/sembo-dates';
import type {
  HeartedHotel,
  HotelBookableOptionRoom,
  HotelBookableOptionWithAlternativeRoom,
  HotelBookableOptionWithRoomsAndDate,
  HotelBookableOptionWithRoomsAndDateAndPrices,
} from '@libs/trip-planner-interfaces';
import {
  getAffiliateCookieData,
  isMetaHotelUser,
  resolveMetaPartner,
} from '@libs/cookie';
import { arrayContainsAllValues } from '@libs/utils';
import { isFeatureToggleActive } from '@libs/feature-toggle';

import { useCustomerFlowStore } from '../customerFlowStore';
import { useEditingIngredientStore } from '../editingIngredient/editingIngredientStore';
import { useItineraryStore } from '../itinerary/itineraryStore';
import { stayMatchesHeartedRate } from '../trip/tripStore.logic';
import { CustomerFlow } from '../../models';

export const useMetaStore = defineStore('MetaStore', () => {
  const customerFlowStore = useCustomerFlowStore();
  const editingIngredientStore = useEditingIngredientStore();
  const itineraryStore = useItineraryStore();

  // State
  const metaAffiliateId = ref<string>();
  const metaClientId = ref<string>();
  const metaDate = ref<Date>();
  const metaHotelCode = ref<string>();
  const metaRoom = ref<string[]>();
  const metaHeartedHotel = ref<HeartedHotel>();
  const metaAffiliateName = ref<string>();

  // Display-only availability for the meta room, resolved by a side-channel
  // search. Never written to the recipe, so it never reaches the URL or the
  // booked itinerary as a selected room.
  const metaRoomStay = ref<HotelBookableOptionWithRoomsAndDateAndPrices>();
  const metaRoomPrice = computed(() => metaRoomStay.value?.totalPrice);
  const metaRoomAlternativeFromSearch =
    ref<HotelBookableOptionWithAlternativeRoom>();
  const metaRoomPriceLoading = ref<boolean>(false);

  // That search re-queries the room pinned by the meta link, so it comes back
  // empty exactly when the room is gone. The itinerary already answers that
  // case with an alternative room at the same hotel.
  const metaRoomAlternative = computed<
    HotelBookableOptionWithAlternativeRoom | undefined
  >(() => {
    if (metaRoomAlternativeFromSearch.value) {
      return metaRoomAlternativeFromSearch.value;
    }

    const ingredientId =
      editingIngredientStore.editingStayIngredient?.ingredientId;
    if (!ingredientId || !metaHotelCode.value) {
      return undefined;
    }

    const alternative =
      itineraryStore.getHotelIngredientResponse(ingredientId)
        ?.alternativeSuggestion ?? undefined;

    return alternative?.code === metaHotelCode.value ? alternative : undefined;
  });

  // Actions
  function setMetaRoomAvailability(
    stay: HotelBookableOptionWithRoomsAndDateAndPrices | undefined,
    alternative: HotelBookableOptionWithAlternativeRoom | undefined
  ): void {
    metaRoomStay.value = stay;
    metaRoomAlternativeFromSearch.value = alternative;
  }

  function setMetaRoomPriceLoading(loading: boolean): void {
    metaRoomPriceLoading.value = loading;
  }

  function clearMetaRoomAvailability(): void {
    metaRoomStay.value = undefined;
    metaRoomAlternativeFromSearch.value = undefined;
    metaRoomPriceLoading.value = false;
  }

  function setMetaInfo(
    metaRooms?: string[],
    initialDate?: Date,
    affiliateName?: string,
    heartedHotel?: HeartedHotel
  ): void {
    const affiliateCookieData = getAffiliateCookieData();

    metaRoom.value = metaRooms;
    metaHeartedHotel.value = heartedHotel;
    metaAffiliateId.value =
      affiliateCookieData?.affiliateCookieParams.AffiliateId;
    metaClientId.value = affiliateCookieData?.affiliateCookieParams.ClientId;
    metaDate.value = initialDate;
    metaAffiliateName.value = affiliateName;

    if (isMetaHotelUser()) {
      customerFlowStore.setCustomerFlow(CustomerFlow.MetaHotel);
    }
  }

  function setMetaHotelCode(code: string | undefined): void {
    metaHotelCode.value = code;
  }

  // Getters
  function stayIsFromMeta(stay: HotelBookableOptionWithRoomsAndDate): boolean {
    const roomsCodes: string[] = stay.rooms.map((room) => room.roomCode);
    return (
      new SemboDate(metaDate.value).isSameDay(stay.date) &&
      metaHotelCode.value === stay.code &&
      arrayContainsAllValues(metaRoom.value ?? [], roomsCodes)
    );
  }

  function shouldShowOfferSourceLabel(stay: {
    rooms: HotelBookableOptionRoom[];
    stayId: { isRefundable: boolean };
  }): boolean {
    if (
      isFeatureToggleActive('as-seen-on-trivago') ||
      isFeatureToggleActive('as-seen-on-kayak')
    ) {
      return true;
    }
    if (!resolveMetaPartner()) {
      return false;
    }
    const heartedHotel = metaHeartedHotel.value;
    return (
      !!heartedHotel &&
      stayMatchesHeartedRate(
        { isRefundable: stay.stayId.isRefundable, rooms: stay.rooms },
        heartedHotel
      )
    );
  }

  return {
    //State
    metaAffiliateId,
    metaClientId,
    metaDate,
    metaHotelCode,
    metaRoom,
    metaHeartedHotel,
    metaAffiliateName,
    metaRoomStay,
    metaRoomPrice,
    metaRoomAlternative,
    metaRoomPriceLoading,

    // Actions
    setMetaInfo,
    setMetaHotelCode,
    setMetaRoomAvailability,
    setMetaRoomPriceLoading,
    clearMetaRoomAvailability,

    // Getters
    stayIsFromMeta,
    shouldShowOfferSourceLabel,
  };
});
